import { DataCenterFinancialSnapshot } from '../../financial/DataCenterFinancialSnapshot';
/**
 * TaxAndCommercialSection (FIN-2) — tax, incentive and commercial-terms scope.
 *
 * READ-ONLY over the persisted stamp. This section's ONE job is to state,
 * accurately, WHAT THE ENGINE MODELLED — because a reader who assumes a
 * post-credit NPV when the number is pre-credit will misprice the project by
 * the entire value of the credit.
 *
 * WHAT THE ENGINE ACTUALLY DOES (`projectFinance.ts`):
 *   - PPA / offtake price IS the revenue basis, CONTRACTED for `ppaTermYears`
 *     and at the merchant tail price thereafter.
 *   - ITC is a one-time credit at the investment year, as a percentage of the
 *     EFFECTIVE capital raised; PTC is a per-MWh credit for the first
 *     `PTC_DURATION_YEARS` operating years. Both flow through NPV / IRR /
 *     payback. They do NOT enter LCOE, which is a pre-credit COST metric.
 *   - Corporate tax itself is still NOT modelled: cash flows are pre-tax and
 *     there is no depreciation shield.
 *
 * HISTORY, because this section has been wrong in both directions: it once
 * asserted that credits were "modeled inside the engine NPV & LCOE" while the
 * engine read neither field (a preference could be toggled and not one number
 * moved). It was corrected to say "not modelled" — and then the credits were
 * genuinely wired in. The rule this section exists to serve is that its text
 * must state what the engine DOES, so it is worth re-reading `projectFinance`
 * before editing it.
 */
import React from 'react';
import type { SiteRagReport } from '../../../../types/siteRag';
import { SectionShell } from '../SectionShell';
import { useEffectiveReportTheme } from '../../../../lib/useEffectiveReportTheme';
import { roleColor } from '../../../../lib/reportRoleColors';
import { EditorialBadge } from '../../report/editorial';
import { FIN_MONO } from '../../../../lib/reportRendering/financialFormat';
import { PTC_DURATION_YEARS } from '../../../../lib/projectFinance';

/** One scope row: what the engine did with a given commercial input. */
interface ScopeRow {
  label: string;
  /** `modelled` = it moves the persisted numbers; `not-modelled` = it does not. */
  state: 'modelled' | 'not-modelled';
  note: string;
}

function isNum(v: number | null | undefined): v is number {
  return typeof v === 'number' && Number.isFinite(v);
}

export const TaxAndCommercialSection: React.FC<{ report: SiteRagReport; label: string }> = ({ report, label }) => {
  const theme = useEffectiveReportTheme();
  const fa = report.financialAnalysis;
  // The declared (but unmodelled) incentive inputs, read from the gen-time
  // snapshot so the section describes the model the report was built on.
  const declared = report.financialAssumptionsAtGen ?? report.financialAssumptionsOverride;
  const itcPct = isNum(declared?.itcPct) ? declared!.itcPct! : null;
  const ptcPerMwh = isNum(declared?.ptcPerMwhUsd) ? declared!.ptcPerMwhUsd! : null;
  const hasDeclaredCredit = itcPct != null || ptcPerMwh != null;
  const hasData = Boolean(fa);

  const creditNote = hasDeclaredCredit
    ? `Declared ${[
      itcPct != null ? `ITC ${itcPct}% of capital` : null,
      ptcPerMwh != null ? `PTC $${ptcPerMwh}/MWh for the first ${PTC_DURATION_YEARS} operating years` : null,
    ].filter(Boolean).join(' · ')} – applied to the NPV, IRR and payback above. LCOE is reported pre-credit, as a cost metric.`
    : 'No ITC / PTC declared on the financial model, so the returns above claim no credit. An eligible project would improve on them.';

  const rows: ScopeRow[] = [
    {
      label: 'PPA / offtake',
      state: 'modelled',
      note: 'The contracted price and escalator are the revenue basis for the PPA term; after it expires the engine sells at the merchant tail price rather than assuming the contract continues.',
    },
    {
      label: 'Corporate tax',
      state: 'not-modelled',
      note: 'Cash flows are PRE-TAX. No tax line and no depreciation shield are applied; the tax rate is used only to derive the after-tax WACC.',
    },
    {
      label: 'IRA ITC / PTC',
      state: hasDeclaredCredit ? 'modelled' : 'not-modelled',
      note: creditNote,
    },
  ];

  if (report.financialAnalysis?.modelKind === 'data-center-capacity-fee') return <SectionShell label={label} hasData><DataCenterFinancialSnapshot stamp={report.financialAnalysis} section="taxAndCommercial" /></SectionShell>;

  return (
    <SectionShell label={label} hasData={hasData}>
      {fa ? (
        <div className="space-y-3">
          <p className="text-[11px]" style={{ color: roleColor(theme, 'muted') }}>
            What the deterministic engine modelled when it produced the figures in this report.
          </p>
          <dl className="text-sm">
            {rows.map((r, i) => (
              <div
                key={r.label}
                className="flex items-start justify-between gap-4 py-2"
                style={{ borderTop: i === 0 ? undefined : `1px solid ${roleColor(theme, 'hair')}` }}
              >
                <dt className="flex flex-col">
                  <span className="font-medium" style={{ color: roleColor(theme, 'ink') }}>{r.label}</span>
                  <span className="text-[11px]" style={{ color: roleColor(theme, 'muted') }}>{r.note}</span>
                </dt>
                <dd className="shrink-0">
                  <EditorialBadge
                    theme={theme}
                    tone={r.state === 'modelled' ? 'positive' : 'neutral'}
                    variant="outline"
                  >
                    {r.state === 'modelled' ? 'modelled' : 'not modelled'}
                  </EditorialBadge>
                </dd>
              </div>
            ))}
          </dl>
          {fa.lcoeEngineDiffNote ? (
            <p className="text-[11px]" style={{ fontFamily: FIN_MONO, color: roleColor(theme, 'muted') }}>
              {fa.lcoeEngineDiffNote}
            </p>
          ) : null}
        </div>
      ) : null}
    </SectionShell>
  );
};
